import React from 'react'
import { TrendingUp } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import DashboardCard from '@/components/dashboard/DashboardCard'
import { movementClass, signedCount, signedMoney } from '@/components/dashboard/Delta'
import { cn } from '@/lib/utils'
import { formatCount, formatMoney } from '@/lib/format'
import { ABBREV_TOOLTIPS, formatPoints } from '@/lib/plain-language'

/**
 * Band 2 — pickup. Four forward windows, each measured over a FIXED target window
 * observed at two points in time, so a negative figure is a genuine net loss
 * (cancellations outpaced new bookings) rather than window drift. Negatives are
 * therefore rendered with their sign and in rose, never as a bare number.
 */

export interface PickupRow {
  key: string
  label: string
  startDate: string
  endDate: string
  roomsOtb: number
  capacityNights: number
  occupancyPct: number
  adr: number
  revpar: number
  revenueOtb: number
  pickupRooms7d: number
  pickupRevenue7d: number
  pickupOccupancyPts7d: number
  pickupSince: string
  pickupWindowDays: number
  currency: string
}

function formatDay(iso: string): string {
  const parsed = new Date(`${iso}T00:00:00`)
  if (Number.isNaN(parsed.getTime())) return iso
  return parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

const numericHead = 'h-10 px-4 text-right text-[11px] font-semibold uppercase tracking-wider text-muted-foreground'

/** "OTB" kept on the surface, but never bare — carries its plain definition on hover. */
function Otb() {
  return (
    <abbr title={ABBREV_TOOLTIPS.OTB} className="cursor-help no-underline decoration-dotted underline-offset-2 hover:underline">
      OTB
    </abbr>
  )
}

export default function PickupTable({ rows }: { rows: PickupRow[] }) {
  const first = rows[0]
  const cur = first.currency
  const anyLoss = rows.some((row) => row.pickupRooms7d < 0 || row.pickupRevenue7d < 0)

  return (
    <DashboardCard
      title="Pickup & on the books"
      description={
        <>
          Confirmed rooms and revenue by window, and what moved in the last{' '}
          {first.pickupWindowDays} days (since {formatDay(first.pickupSince)})
        </>
      }
      icon={<TrendingUp className="h-4 w-4 shrink-0 text-muted-foreground" />}
      help={{
        content:
          'Both sides of every pickup figure cover the same stay dates — only the observation point moves. A negative number is therefore a real net loss for that window: cancellations outpaced new bookings over the last seven days.',
      }}
      padded={false}
      footer={
        anyLoss ? (
          <p className="text-[11px] leading-relaxed text-muted-foreground">
            A negative pickup is a genuine net loss for that window — cancellations outpaced new
            bookings over the last {first.pickupWindowDays} days. It is never an artefact of the
            comparison: both sides cover identical stay dates.
          </p>
        ) : undefined
      }
    >
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="h-10 px-4 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Window
            </TableHead>
            <TableHead className={numericHead}>Occupancy</TableHead>
            <TableHead className={numericHead}>Rooms <Otb /></TableHead>
            <TableHead className={numericHead}>Revenue <Otb /></TableHead>
            <TableHead className={numericHead}>Pickup {first.pickupWindowDays}d rooms</TableHead>
            <TableHead className={numericHead}>Pickup {first.pickupWindowDays}d revenue</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.key}>
              <TableCell className="px-4 py-3">
                <p className="text-sm font-medium text-foreground">{row.label}</p>
                <p className="text-xs text-muted-foreground tabular-nums">
                  {formatDay(row.startDate)} – {formatDay(row.endDate)}
                </p>
              </TableCell>

              <TableCell className="px-4 py-3 text-right">
                <p className="text-sm font-semibold text-foreground tabular-nums">
                  {row.occupancyPct}%
                </p>
                <p className="text-xs text-muted-foreground tabular-nums">
                  {formatCount(row.capacityNights)} available
                </p>
              </TableCell>

              <TableCell className="px-4 py-3 text-right text-sm font-semibold text-foreground tabular-nums">
                {formatCount(row.roomsOtb)}
              </TableCell>

              <TableCell className="px-4 py-3 text-right">
                <p className="text-sm font-semibold text-foreground tabular-nums">
                  {formatMoney(row.revenueOtb, cur)}
                </p>
                <p className="text-xs text-muted-foreground tabular-nums">
                  ADR {formatMoney(row.adr, cur)}
                </p>
              </TableCell>

              <TableCell className="px-4 py-3 text-right">
                {/* Rooms is the lead signal — operators count rooms — so it is the bold
                    primary figure; the occupancy points delta is the subordinate context. */}
                <p
                  className={cn(
                    'text-sm font-semibold tabular-nums',
                    movementClass(row.pickupRooms7d)
                  )}
                >
                  {signedCount(row.pickupRooms7d)} {Math.abs(row.pickupRooms7d) === 1 ? 'room' : 'rooms'}
                </p>
                <p
                  className={cn(
                    'text-xs tabular-nums',
                    row.pickupOccupancyPts7d === 0
                      ? 'text-muted-foreground'
                      : movementClass(row.pickupOccupancyPts7d)
                  )}
                >
                  {formatPoints(row.pickupOccupancyPts7d)} occupancy
                </p>
              </TableCell>

              <TableCell
                className={cn(
                  'px-4 py-3 text-right text-sm font-semibold tabular-nums',
                  movementClass(row.pickupRevenue7d)
                )}
              >
                {signedMoney(row.pickupRevenue7d, cur)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </DashboardCard>
  )
}
